import React, { useEffect, useState, useCallback } from 'react';
import { Zap, Activity, Server, Shield, Database, Key, Webhook, TrendingUp, Loader2, Plus, Copy } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { api, BACKEND_URL } from './api';
import { addressLine } from './shared/address';
import { useTabParam } from './shared/useTabParam';
import { errorMessage } from './errors';
import { copyText } from './clipboard';
import LeadFormEditor from './LeadFormEditor';
import ApiKeyDialog from './ApiKeyDialog';
import WebhookDialog from './WebhookDialog';
import LeadCapturesList from './LeadCapturesList';
import LongTextCell from '@/components/LongTextCell';
import { TableScroller } from '@/components/ui/table-scroller';

const GHL_SYNC_LABEL = { ok: 'Synced', stale: 'Stale', off: 'Off' };

export default function EnterprisePage() {
  const [tab, setTab] = useTabParam('overview', { valid: ['overview', 'ai', 'leadgen', 'developers'] });
  const [ent, setEnt] = useState(null);
  const [usage, setUsage] = useState(null);
  const [seats, setSeats] = useState(null);
  const [health, setHealth] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [aiActions, setAiActions] = useState([]);
  const [stalled, setStalled] = useState([]);
  const [forms, setForms] = useState([]);
  const [staff, setStaff] = useState(null);
  const [editor, setEditor] = useState(null);
  const [attribution, setAttribution] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scopes, setScopes] = useState([]);
  const [keyDialog, setKeyDialog] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [events, setEvents] = useState([]);
  const [webhookDialog, setWebhookDialog] = useState(false);
  const [newSecret, setNewSecret] = useState(null);
  const [logFor, setLogFor] = useState(null);
  const [log, setLog] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, u, s, h, ak, wh, aa, st, lf, ar] = await Promise.all([
        api.get('/v2/enterprise/entitlements').then(r => r.data),
        api.get('/v2/enterprise/usage').then(r => r.data),
        api.get('/v2/enterprise/seats').then(r => r.data),
        api.get('/v2/enterprise/health').then(r => r.data).catch(() => null),
        api.get('/v2/enterprise/api-keys').then(r => r.data).catch(() => ({ items: [], available_scopes: [] })),
        api.get('/v2/enterprise/webhooks').then(r => r.data).catch(() => ({ items: [], available_events: [] })),
        api.get('/v2/ai/actions?limit=10').then(r => r.data).catch(() => ({ items: [] })),
        api.get('/v2/ai/stalled-deals').then(r => r.data).catch(() => ({ flagged: [] })),
        api.get('/v2/leadgen/forms').then(r => r.data).catch(() => ({ items: [] })),
        api.get('/v2/leadgen/attribution/report').then(r => r.data).catch(() => ({ by_source: [] })),
      ]);
      setEnt(e); setUsage(u); setSeats(s); setHealth(h);
      setApiKeys(ak.items || []); setScopes(ak.available_scopes || []); setWebhooks(wh.items || []); setEvents(wh.available_events || []);
      setAiActions(aa.items || []); setStalled(st.flagged || []);
      setForms(lf.items || []); setAttribution(ar.by_source || []);
    } catch (err) { toast({ title: 'Could not load the Enterprise page', description: errorMessage(err) }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createApiKey = async ({ name, scopes: picked }) => {
    const r = await api.post('/v2/enterprise/api-keys', { name, scopes: picked });
    setKeyDialog(false);
    setNewKey({ name, raw_key: r.data.raw_key });
    if (await copyText(r.data.raw_key)) toast({ title: 'API key created + copied to clipboard' });
    else toast({ title: 'API key created', description: 'Copy failed — select the text and copy it manually' });
    load();
  };

  const revokeKey = async (k) => {
    if (!(await confirmDialog({
      title: `Revoke "${k.name}"?`,
      description: 'Anything using this key stops working right away. This cannot be undone.',
      confirmText: 'Revoke key',
      destructive: true,
    }))) return;
    try {
      await api.delete(`/v2/enterprise/api-keys/${k.key_id}`);
      toast({ title: 'API key revoked' });
      load();
    } catch (e) { toast({ title: 'Revoke failed', description: errorMessage(e) }); }
  };

  const rotateKey = async (k) => {
    if (!(await confirmDialog({
      title: `Rotate "${k.name}"?`,
      description: 'You get a new secret now. The old secret keeps working for 24 hours, then stops.',
      confirmText: 'Rotate key',
    }))) return;
    try {
      const r = await api.post(`/v2/enterprise/api-keys/${k.key_id}/rotate`);
      setNewKey({ name: k.name, raw_key: r.data.raw_key });
      if (await copyText(r.data.raw_key)) toast({ title: 'New key created + copied to clipboard' });
      else toast({ title: 'New key created', description: 'Copy failed — select the text and copy it manually' });
      load();
    } catch (e) { toast({ title: 'Rotate failed', description: errorMessage(e) }); }
  };

  const deleteWebhook = async (w) => {
    if (!(await confirmDialog({
      title: 'Delete this webhook?',
      description: `${w.url} stops receiving events right away.`,
      confirmText: 'Delete webhook',
      destructive: true,
    }))) return;
    try {
      await api.delete(`/v2/enterprise/webhooks/${w.subscription_id}`);
      toast({ title: 'Webhook deleted' });
      load();
    } catch (e) { toast({ title: 'Delete failed', description: errorMessage(e) }); }
  };

  const loadLog = async (id) => {
    setLog(null);
    try { setLog((await api.get(`/v2/enterprise/webhooks/${id}/deliveries`)).data.items || []); }
    catch (e) { setLog([]); toast({ title: 'Could not load the delivery log', description: errorMessage(e) }); }
  };

  const toggleLog = (w) => {
    if (logFor === w.subscription_id) { setLogFor(null); return; }
    setLogFor(w.subscription_id);
    loadLog(w.subscription_id);
  };

  const testWebhook = async (w) => {
    try {
      const d = (await api.post(`/v2/enterprise/webhooks/${w.subscription_id}/test`)).data;
      if (d.succeeded) toast({ title: 'Test delivered', description: `${w.url} answered ${d.status_code}` });
      else toast({ title: 'Test failed', description: d.error || `${w.url} answered ${d.status_code}` });
    } catch (e) { toast({ title: 'Test failed', description: errorMessage(e) }); }
    if (logFor === w.subscription_id) loadLog(w.subscription_id);
  };

  const createWebhook = async ({ url, events: picked }) => {
    const r = await api.post('/v2/enterprise/webhooks', { url, events: picked });
    setWebhookDialog(false);
    setNewSecret({ url, secret: r.data.secret });
    if (await copyText(r.data.secret)) toast({ title: 'Webhook added + secret copied to clipboard' });
    else toast({ title: 'Webhook added', description: 'Copy failed — select the secret and copy it manually' });
    load();
  };

  // `editor` = null (closed) | { form: null } (new) | { form } (edit)
  const openEditor = async (form) => {
    if (staff === null) {
      try { setStaff((await api.get('/crm/staff')).data.items || []); }
      catch (e) { toast({ title: 'Could not load staff', description: errorMessage(e) }); return; }
    }
    setEditor({ form });
  };

  const hostedLink = (slug) => `${window.location.origin}/f/${slug}`;

  // embed.js lives on the site's own origin; the form data comes from the API, which is a
  // different host in production, so the snippet names it (omitted when they are the same).
  const embedSnippet = (slug) => `<script src="${window.location.origin}/embed.js" data-slug="${slug}"${BACKEND_URL ? ` data-api="${BACKEND_URL}"` : ''}></script>`;

  return (
    <div className="p-4 md:p-8" data-testid="enterprise-page">
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-bold" style={{ color: '#070B5F' }} data-testid="enterprise-title">Enterprise Console</h1>
        <p className="text-slate-500 mt-1" data-testid="enterprise-subtitle">Audrey AI · Lead generation · Seats · API keys · Webhooks · System health</p>
      </div>

      <div className="border-b border-slate-100 flex gap-1 overflow-x-auto mb-6">
        {[
          { k: 'overview', l: 'Overview' },
          { k: 'ai', l: 'Audrey AI' },
          { k: 'leadgen', l: 'Lead Gen' },
          { k: 'developers', l: 'API + Webhooks' },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`ent-tab-${t.k}`}
            className="px-4 py-2 text-sm border-b-2 -mb-px transition"
            style={tab === t.k ? { borderColor: '#070B5F', color: '#070B5F', fontWeight: 500 } : { borderColor: 'transparent', color: '#64748b' }}
          >
            {t.l}
          </button>
        ))}
      </div>

      {loading ? <Loader className="text-slate-400" /> : tab === 'overview' ? (
        <div className="grid md:grid-cols-3 gap-4">
          <StatTile title="Subscription" icon={Shield} data-testid="tile-tier">
            <div className="font-serif stat-num text-3xl font-bold" style={{ color: '#070B5F' }}>{ent?.tier || '—'}</div>
            <div className="text-xs text-slate-500 mt-1">{ent?.features?.length || 0} feature(s) enabled</div>
          </StatTile>
          <StatTile title="Seats" icon={Database} data-testid="tile-seats">
            <div className="font-serif stat-num text-3xl font-bold" style={{ color: '#070B5F' }}>
              {seats?.used || 0}<span className="text-lg text-slate-400 font-sans font-normal"> / {seats?.limit || 0}</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">{seats?.available || 0} available</div>
          </StatTile>
          <StatTile title="This Period" icon={Activity} data-testid="tile-usage">
            <div className="text-xs text-slate-600 space-y-1.5 mt-2">
              <div className="flex justify-between"><span>AI tokens</span><span className="font-semibold" style={{ color: '#070B5F' }}>{usage?.ai_tokens || 0}</span></div>
              <div className="flex justify-between"><span>SMS</span><span className="font-semibold" style={{ color: '#070B5F' }}>{usage?.sms_count || 0}</span></div>
              <div className="flex justify-between"><span>Emails</span><span className="font-semibold" style={{ color: '#070B5F' }}>{usage?.email_count || 0}</span></div>
            </div>
          </StatTile>
          <StatTile title="System Health" icon={Server} data-testid="tile-health">
            {health && (
              <div className="text-xs space-y-1.5 mt-2">
                <HealthRow k="MongoDB" ok={health.checks.mongo === 'ok'} critical label={health.checks.mongo === 'ok' ? 'Healthy' : 'Down'} />
                <HealthRow k="GoHighLevel sync" ok={health.checks.ghl_sync === 'ok'} critical={health.checks.ghl_sync === 'stale'} label={GHL_SYNC_LABEL[health.checks.ghl_sync] || 'Off'} />
                <HealthRow k="GoHighLevel texts" ok={health.checks.ghl_send_ready} label={health.checks.ghl_send_ready ? 'Live' : 'Off'} />
                <HealthRow k="Mailgun" ok={health.checks.mailgun_configured} label={health.checks.mailgun_configured ? 'Live' : 'Mocked'} />
                <HealthRow k="Audrey AI" ok={health.checks.llm_configured} label={health.checks.llm_configured ? (health.checks.llm_provider || 'Configured') : 'Mocked'} />
              </div>
            )}
          </StatTile>
        </div>
      ) : tab === 'ai' ? (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
            <div className="flex items-center gap-2 mb-2" style={{ color: '#070B5F' }}><TrendingUp size={14} /> <strong className="text-sm">Stalled Deals ({stalled.length})</strong></div>
            {stalled.length === 0 ? <p className="text-xs text-slate-400">All deals moving on time.</p> :
              <ul className="text-xs space-y-1">
                {stalled.map(s => (
                  <li key={s.opportunity_id} className="flex justify-between border-b border-slate-100 py-1">
                    <span>{s.label || addressLine(s.property_address) || 'Unnamed deal'}</span>
                    <span className="text-red-500">{s.days_in_stage}d in <strong>{s.stage_label || s.stage}</strong> (threshold {s.threshold}d)</span>
                  </li>
                ))}
              </ul>}
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
            <strong className="text-sm" style={{ color: '#070B5F' }}>Recent AI Actions</strong>
            {aiActions.length === 0 ? <p className="text-xs text-slate-400 mt-2">No AI actions yet.</p> :
              <ul className="mt-2 text-xs divide-y divide-slate-100">
                {aiActions.map(a => (
                  <li key={a.action_id} className="py-2 flex items-center justify-between">
                    <span>{a.kind} · {a.source_type || '—'}</span>
                    <span className="text-slate-400">{a.output_tokens} tok · ${(a.cost_usd || 0).toFixed(4)}</span>
                  </li>
                ))}
              </ul>}
          </div>
        </div>
      ) : tab === 'leadgen' ? (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-medium" style={{ color: '#070B5F' }}>Landing forms ({forms.length})</h3>
            <button onClick={() => openEditor(null)} data-testid="new-form-btn" className="btn-gold">
              <Plus size={16} /> New form
            </button>
          </div>
          {forms.length === 0 ? <p className="text-xs text-slate-400">No forms yet.</p> : (
            <ul className="space-y-2">
              {forms.map(f => (
                <li key={f.form_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium" style={{ color: '#070B5F' }}>
                        {f.name} <span className="text-xs text-slate-400">/{f.slug}</span>
                        <span data-testid={`form-status-${f.form_id}`} className={`ml-2 text-[10px] uppercase tracking-wider font-bold ${f.active === false ? 'text-slate-400' : 'text-emerald-600'}`}>{f.active === false ? 'Paused' : 'Live'}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{f.kind} · {f.fields?.length || 0} fields · pool: {f.round_robin_pool?.length || 0} users{f.cost_per_lead_cents != null ? ` · $${(f.cost_per_lead_cents / 100).toFixed(2)}/lead` : ''}</div>
                    </div>
                    <button onClick={() => openEditor(f)} data-testid={`edit-form-${f.form_id}`} className="text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50" style={{ color: '#070B5F' }}>Edit</button>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <code data-testid={`hosted-link-${f.form_id}`} className="text-[10px] bg-slate-50 rounded px-2 py-1 flex-1 overflow-x-auto whitespace-nowrap">{hostedLink(f.slug)}</code>
                    <button onClick={async () => { if (await copyText(hostedLink(f.slug))) toast({ title: 'Form link copied' }); else toast({ title: 'Copy failed', description: 'Select the text and copy it manually' }); }}
                      className="p-2 hover:bg-slate-100 rounded-lg" title="Copy form link"><Copy size={14} style={{ color: '#070B5F' }} /></button>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="text-[10px] bg-slate-50 rounded px-2 py-1 flex-1 overflow-x-auto whitespace-nowrap">{embedSnippet(f.slug)}</code>
                    <button onClick={async () => { if (await copyText(embedSnippet(f.slug))) toast({ title: 'Embed snippet copied' }); else toast({ title: 'Copy failed', description: 'Select the text and copy it manually' }); }}
                      className="p-2 hover:bg-slate-100 rounded-lg"><Copy size={14} style={{ color: '#070B5F' }} /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
            <strong className="text-sm" style={{ color: '#070B5F' }}>Attribution report</strong>
            {attribution.length === 0 ? <p className="text-xs text-slate-400 mt-2">No captures yet.</p> :
              <TableScroller className="mt-2">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                      <th className="py-2">Source</th><th className="py-2 pl-3 text-right min-w-[4.5rem]">Leads</th><th className="py-2 pl-3 text-right min-w-[4.5rem]">Closed</th><th className="py-2 pl-3 text-right min-w-[5.5rem]">Cost/Lead</th><th className="py-2 pl-3 text-right min-w-[6.5rem]">Cost/Closing</th>
                    </tr>
                  </thead>
                  <tbody>{attribution.map(a => (
                    <tr key={a.source} className="border-b border-slate-50">
                      <td className="py-2"><LongTextCell id={`source-${a.source}`} text={a.source} lines={2} maxWidth={160} minWidth="8rem" /></td><td className="py-2 text-right">{a.leads}</td><td className="py-2 text-right">{a.closed}</td>
                      <td className="py-2 text-right">${a.cost_per_lead.toFixed(2)}</td>
                      <td className="py-2 text-right">${a.cost_per_closing.toFixed(2)}</td>
                    </tr>))}</tbody>
                </table>
              </TableScroller>}
          </div>
          <LeadCapturesList forms={forms} />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2" style={{ color: '#070B5F' }}><Key size={14} /><strong className="text-sm">API Keys</strong></div>
              <button onClick={() => setKeyDialog(true)} data-testid="new-api-key-btn" className="btn-gold" title="Create a new API key">
                <Plus size={16} /> Issue key
              </button>
            </div>
            {newKey && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3" data-testid="new-api-key-banner">
                <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700 mb-1">
                  "{newKey.name}" — copy this key now, it will not be shown again
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-white rounded px-2 py-1 flex-1 overflow-x-auto whitespace-nowrap border border-amber-200">{newKey.raw_key}</code>
                  <button onClick={async () => { if (await copyText(newKey.raw_key)) toast({ title: 'Copied' }); else toast({ title: 'Copy failed', description: 'Select the text and copy it manually' }); }}
                    className="p-2 hover:bg-amber-100 rounded-lg" title="Copy key"><Copy size={14} className="text-amber-700" /></button>
                  <button onClick={() => setNewKey(null)} data-testid="dismiss-new-api-key-btn"
                    className="text-xs font-semibold text-amber-700 hover:text-amber-900 px-2">Done</button>
                </div>
              </div>
            )}
            {apiKeys.length === 0 ? <p className="text-xs text-slate-400">No API keys yet.</p> : (
              <ul className="text-xs divide-y divide-slate-100">
                {apiKeys.map(k => (
                  <li key={k.key_id} className="py-2 flex items-center justify-between gap-2" data-testid={`api-key-${k.key_id}`}>
                    <span className="min-w-0">
                      {k.name} <code className="text-slate-400">{k.prefix}…</code>
                      <span data-testid={`api-key-status-${k.key_id}`} className={`ml-2 text-[10px] uppercase tracking-wider font-bold ${k.status === 'active' ? 'text-emerald-600' : 'text-slate-400'}`}>{k.status}</span>
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-slate-400">{k.scopes?.join(', ')}</span>
                      {k.status === 'active' && (
                        <button onClick={() => rotateKey(k)} data-testid={`rotate-api-key-${k.key_id}`} className="font-medium px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-50" style={{ color: '#070B5F' }}>Rotate</button>
                      )}
                      {k.status === 'active' && (
                        <button onClick={() => revokeKey(k)} data-testid={`revoke-api-key-${k.key_id}`} className="font-medium px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-50 text-red-600">Revoke</button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2" style={{ color: '#070B5F' }}><Webhook size={14} /><strong className="text-sm">Webhooks</strong></div>
              <button onClick={() => setWebhookDialog(true)} data-testid="new-webhook-btn" className="btn-gold"><Plus size={16} /> Add</button>
            </div>
            {newSecret && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3" data-testid="new-webhook-secret-banner">
                <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700 mb-1 break-all">
                  Signing secret for {newSecret.url} — copy it now, it will not be shown again
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-white rounded px-2 py-1 flex-1 overflow-x-auto whitespace-nowrap border border-amber-200">{newSecret.secret}</code>
                  <button onClick={async () => { if (await copyText(newSecret.secret)) toast({ title: 'Copied' }); else toast({ title: 'Copy failed', description: 'Select the text and copy it manually' }); }}
                    className="p-2 hover:bg-amber-100 rounded-lg" title="Copy secret"><Copy size={14} className="text-amber-700" /></button>
                  <button onClick={() => setNewSecret(null)} data-testid="dismiss-new-webhook-secret-btn"
                    className="text-xs font-semibold text-amber-700 hover:text-amber-900 px-2">Done</button>
                </div>
              </div>
            )}
            {webhooks.length === 0 ? <p className="text-xs text-slate-400">No webhooks yet.</p> : (
              <ul className="text-xs divide-y divide-slate-100">
                {webhooks.map(w => (
                  <li key={w.subscription_id} className="py-2" data-testid={`webhook-${w.subscription_id}`}>
                    <div className="flex items-center justify-between gap-2">
                    <span className="truncate">{w.url}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-slate-400">{w.events?.join(', ')}</span>
                      <button onClick={() => toggleLog(w)} data-testid={`log-webhook-${w.subscription_id}`} className="font-medium px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-50" style={{ color: '#070B5F' }}>{logFor === w.subscription_id ? 'Hide log' : 'Log'}</button>
                      <button onClick={() => testWebhook(w)} data-testid={`test-webhook-${w.subscription_id}`} className="font-medium px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-50" style={{ color: '#070B5F' }}>Send test</button>
                      <button onClick={() => deleteWebhook(w)} data-testid={`delete-webhook-${w.subscription_id}`} className="font-medium px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-50 text-red-600">Delete</button>
                    </span>
                    </div>
                    {logFor === w.subscription_id && (
                      <div className="mt-2" data-testid={`webhook-log-${w.subscription_id}`}>
                        {log === null ? <Loader2 className="animate-spin text-slate-400" size={14} /> : log.length === 0 ? <p className="text-slate-400">No deliveries yet. Send a test to see one here.</p> : (
                          <TableScroller>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                                  <th className="py-1">Sent</th><th className="py-1 pl-3">Event</th><th className="py-1 pl-3">Result</th>
                                </tr>
                              </thead>
                              <tbody>{log.map(d => (
                                <tr key={d.delivery_id} className="border-b border-slate-50" data-testid={`delivery-${d.delivery_id}`}>
                                  <td className="py-1 whitespace-nowrap">{new Date(d.sent_at).toLocaleString()}</td>
                                  <td className="py-1 pl-3">{d.event}</td>
                                  <td className="py-1 pl-3" style={{ color: d.succeeded ? '#6BA82E' : '#ef4444' }}>
                                    {d.succeeded ? `Delivered${d.status_code ? ` (${d.status_code})` : ''}` : d.error || `Failed${d.status_code ? ` (${d.status_code})` : ''}`}
                                  </td>
                                </tr>))}</tbody>
                            </table>
                          </TableScroller>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      <WebhookDialog open={webhookDialog} events={events} onClose={() => setWebhookDialog(false)} onSubmit={createWebhook} />
      <ApiKeyDialog open={keyDialog} scopes={scopes} onClose={() => setKeyDialog(false)} onSubmit={createApiKey} />
      {editor && (
        <LeadFormEditor open form={editor.form} staff={staff || []} onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); load(); }} />
      )}
    </div>
  );
}

const StatTile = ({ title, icon: Icon, children, ...rest }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" {...rest}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1"><Icon size={12} /> {title}</div>
    {children}
  </div>
);

const HealthRow = ({ k, ok, label, critical }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-600">{k}</span>
    <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: ok ? '#6BA82E' : critical ? '#ef4444' : '#94a3b8' }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: ok ? '#6BA82E' : critical ? '#ef4444' : '#94a3b8' }} />
      {label}
    </span>
  </div>
);

const Loader = ({ className }) => (
  <div className="p-16 flex items-center justify-center">
    <Loader2 className={`animate-spin ${className}`} size={20} />
  </div>
);
